import { MONEY_PRIVACY_CLASS } from "../../../utils/moneyPrivacy";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "../../../context/ThemeContext";
import WorkHoursHeatmap from "../components/WorkHoursHeatmap";
import AllocationCard from "../components/AllocationCard";
import SummaryNotices from "../components/SummaryNotices";
import ClientScopeSelect from "../components/ClientScopeSelect";
import ExpectedKpis, { ExpectedBreakdownTable, GapBadge, GapWithPending, useExpectedRuleText } from "../components/ExpectedKpis";
import ExpectedCostCard from "../components/ExpectedCostCard";
import RepoBreakdown from "../components/RepoBreakdown";
import BridgeCard from "../components/BridgeCard";
import PullRequestsCard from "../components/PullRequestsCard";
import KindHoursTable from "../components/KindHoursTable";
import { FxNote, MxnEquivalent } from "../components/MxnEquivalent";
import { money } from "../components/ExpectedKpis";
import { hoursOf, type Ledger } from "../lib/ledger";
import type { ActivityType } from "@torre/work-hours";
import {
  fetchActivityCatalog,
  fetchDetail,
  fetchAllocations,
  fetchEntries,
  fetchMe,
  fetchSummary,
  type AllocationResponse,
  type HourEntry,
  type SummaryResponse,
  type WorkHoursMe,
} from "../services/workHoursService";
import {
  dateTimeLabel,
  fillDays,
  hourLabel,
  localeOf,
  monthLabel,
  payPeriodPreset,
  presetRange,
  rangeDays,
  today,
  weekLabel,
  weekdayLabel,
  type RangePreset,
} from "../lib/heatmap";
import { commitUrl, pullRequestUrl, repoUrl } from "../lib/github";
import { groupCommitsByRepo, plainCommitMessage, prKey, summarizeHourDetail } from "../lib/commits";
import { readFailureKey } from "../lib/requestFailure";
import { buildThemeClasses, inputClass } from "../theme";

// ---------------------------------------------------------------------------
// Dashboard de Horas Trabajadas (#537): KPIs, mapa de calor 7×24, horas por
// día/semana/mes, personas, repos y PRs. Un dev ve solo lo suyo; un admin elige
// entre el equipo completo o una persona. Toda hora enlaza a sus commits.
// ---------------------------------------------------------------------------

type Series = "day" | "week" | "month";

/** Pestañas de la tarjeta «Desglose de horas», en el orden en que se pintan. */
type BreakdownTab = "squad" | "project" | "person" | "kind" | "repo";
const BREAKDOWN_TABS: { id: BreakdownTab; label: string }[] = [
  { id: "squad", label: "workHours.expected.bySquad" },
  { id: "project", label: "workHours.expected.byProject" },
  { id: "person", label: "workHours.people.title" },
  { id: "kind", label: "workHours.byKind.title" },
  { id: "repo", label: "workHours.repos.title" },
];

export default function DashboardSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const locale = localeOf(i18n?.language);

  const [me, setMe] = useState<WorkHoursMe | null>(null);
  const [preset, setPreset] = useState<RangePreset>("30d");
  const [range, setRange] = useState(presetRange("30d"));
  const [userId, setUserId] = useState("");
  const [repo, setRepo] = useState("");
  // #978: en CC-CONSOLE el tablero muestra todos los clientes; el admin acota a uno.
  const [clientId, setClientId] = useState("");
  // Squad (solo admin): acota TODO el tablero a su gente; `none` = sin squad.
  // Las opciones son las de la última respuesta que las trajo: no desaparecen
  // mientras carga —o falla— una consulta ya filtrada.
  const [squadId, setSquadId] = useState("");
  const [squadOptions, setSquadOptions] = useState<NonNullable<SummaryResponse["squads"]> | null>(null);
  const [series, setSeries] = useState<Series>("week");
  const [breakdownTab, setBreakdownTab] = useState<BreakdownTab>("person");
  const [data, setData] = useState<SummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Se guarda la CLAVE i18n, no el texto: así `load` no depende de `t` (que
  // react-i18next reemplaza al cargar un namespace o al cambiar de idioma) y el
  // mensaje se traduce solo al cambiar de idioma.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [cell, setCell] = useState<{ weekday: number; hour: number; hours: number } | null>(null);
  const [entries, setEntries] = useState<HourEntry[] | null>(null);
  const [entriesLoading, setEntriesLoading] = useState(false);
  // El detalle tiene TRES estados, no dos: cargando, error y vacío de verdad.
  // Antes el `catch` vaciaba la lista y un 503 se leía igual que «no hay horas
  // en esa celda»: la pantalla mentía justo cuando el dato no llegó.
  const [entriesErrorKey, setEntriesErrorKey] = useState<string | null>(null);
  const [allocation, setAllocation] = useState<AllocationResponse | null>(null);
  const [allocationErrorKey, setAllocationErrorKey] = useState<string | null>(null);
  const [allocationReload, setAllocationReload] = useState(0);

  useEffect(() => {
    fetchMe().then(setMe).catch(() => setMe(null));
  }, []);

  // #1344: el libro —las líneas detrás de cada cifra— se pide DESPUÉS del resumen
  // y con los mismos filtros, para no frenar el tablero. Nunca se empareja un
  // resumen con el libro de otra consulta: si el resumen falla, lo que queda en
  // pantalla es el anterior y sus renglones no se despliegan; si falla el libro,
  // se avisa y «Recargar» lo vuelve a pedir. Una respuesta vieja no pisa a una nueva.
  const [detail, setDetail] = useState<Ledger | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [detailTooLarge, setDetailTooLarge] = useState(false);
  const requestRef = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestRef.current;
    const params = {
      from: range.from,
      to: range.to,
      userId: userId || undefined,
      repo: repo || undefined,
      clientId: clientId || undefined,
      squadId: squadId || undefined,
    };
    setLoading(true);
    setErrorKey(null);
    setDetail(null);
    setDetailFailed(false);
    setDetailTooLarge(false);
    try {
      const res = await fetchSummary(params);
      if (id !== requestRef.current) return;
      setData(res);
      if (res?.squads) setSquadOptions(res.squads);
      fetchDetail(params)
        .then((d) => {
          if (id !== requestRef.current) return;
          // Rango demasiado largo para todo el equipo: llega vacío y se avisa.
          setDetailTooLarge(Boolean(d?.detail?.tooLarge));
          setDetail(d?.detail && !d.detail.tooLarge ? d.detail : null);
        })
        .catch(() => {
          if (id === requestRef.current) setDetailFailed(true);
        });
    } catch (e) {
      if (id !== requestRef.current) return;
      // Un corte por tiempo no es un fallo definitivo: el dato no llegó, que no
      // es lo mismo que no exista. Se dice distinto y se ofrece recargar.
      setErrorKey(readFailureKey(e, { failed: "workHours.loadError", timeout: "workHours.timeoutError" }));
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [range.from, range.to, userId, repo, clientId, squadId]);

  useEffect(() => {
    void load();
  }, [load]);

  // #1342: nombres de los tipos de actividad (catálogo de la empresa) para la
  // tabla «Por tipo». Solo se pide si el resumen la trae; sin catálogo, la tabla
  // cae a las traducciones de los tipos base.
  const [kindCatalog, setKindCatalog] = useState<ActivityType[] | null>(null);
  const hasKinds = Boolean(data?.summary.byKind?.length);
  useEffect(() => {
    if (!hasKinds || kindCatalog) return;
    fetchActivityCatalog()
      .then((res) => setKindCatalog(res.data))
      .catch(() => setKindCatalog([]));
  }, [hasKinds, kindCatalog]);

  // La celda abierta solo se cierra cuando cambia DE VERDAD el filtro: antes se
  // limpiaba en el efecto de `load`, que se redisparaba con cada `t` nueva y
  // cerraba el detalle sin que el usuario tocara nada.
  useEffect(() => {
    setCell(null);
    setEntries(null);
    setEntriesErrorKey(null);
  }, [range.from, range.to, userId, repo, clientId, squadId]);

  // Asignación a proyectos: la propia (dev) o la de la persona elegida (admin).
  useEffect(() => {
    if (!me || (me.isAdmin && !userId)) {
      setAllocation(null);
      setAllocationErrorKey(null);
      return;
    }
    let cancelled = false;
    setAllocationErrorKey(null);
    fetchAllocations({ from: range.from, to: range.to, userId: userId || undefined })
      .then((a) => {
        if (cancelled) return;
        setAllocation(a);
        setAllocationErrorKey(null);
      })
      // Si falla no se pinta «elige una persona» (ya está elegida): se dice que
      // no se pudo cargar y se ofrece reintentar.
      .catch((e) => {
        if (cancelled) return;
        setAllocation(null);
        setAllocationErrorKey(readFailureKey(e, { failed: "workHours.allocation.error", timeout: "workHours.allocation.timeout" }));
      });
    return () => {
      cancelled = true;
    };
  }, [me, userId, range.from, range.to, allocationReload]);

  const openCell = async (c: { weekday: number; hour: number; hours: number }) => {
    setCell(c);
    setEntriesErrorKey(null);
    if (c.hours === 0) {
      setEntries([]);
      return;
    }
    setEntriesLoading(true);
    try {
      setEntries(
        await fetchEntries({
          from: range.from,
          to: range.to,
          userId: userId || undefined,
          repo: repo || undefined,
          clientId: clientId || undefined,
          squadId: squadId || undefined,
          weekday: c.weekday,
          hour: c.hour,
        }),
      );
    } catch (e) {
      // Un fallo NO es una celda vacía: se borra la lista y se marca el error.
      // Y un corte por tiempo tampoco es un fallo definitivo: se dice aparte.
      setEntries(null);
      setEntriesErrorKey(readFailureKey(e, { failed: "workHours.detail.error", timeout: "workHours.detail.timeout" }));
    } finally {
      setEntriesLoading(false);
    }
  };

  const digest = useMemo(() => summarizeHourDetail(entries), [entries]);
  const commitGroups = useMemo(() => groupCommitsByRepo(entries), [entries]);
  const hasDetail = !entriesLoading && !entriesErrorKey && !!entries && entries.length > 0;

  const summary = data?.summary;
  const chartData = useMemo(() => {
    if (!summary) return [];
    if (series === "day") return fillDays(summary.byDay, range.from, range.to).map((d) => ({ label: d.date.slice(5), hours: d.hours }));
    if (series === "week") return summary.weekly.map((w) => ({ label: weekLabel(w.weekStart, locale), hours: w.hours }));
    return summary.monthly.map((m) => ({ label: monthLabel(m.month, locale), hours: m.hours }));
  }, [summary, series, range.from, range.to, locale]);

  // #969: esperado a hoy vs real por persona. La tabla suma a quien espera horas
  // y no registró ninguna en el rango (su faltante es justo lo que hay que ver).
  const expected = summary?.expected;
  const expectedByUser = useMemo(() => new Map((expected?.byPerson ?? []).map((p) => [p.userId, p])), [expected]);
  const expectedRuleText = useExpectedRuleText(expected?.rule);
  const peopleRows = useMemo(() => {
    if (!summary) return [];
    const seen = new Set(summary.byUser.map((u) => u.userId));
    const missing = (expected?.byPerson ?? [])
      .filter((p) => !seen.has(p.userId))
      .map((p) => ({ userId: p.userId, userName: p.name, hours: 0, days: 0, commits: 0, firstHour: null as number | null, lastHour: null as number | null }));
    return [...summary.byUser, ...missing];
  }, [summary, expected]);

  // «Real» solo suma a quien entra al esperado; el KPI «Horas» suma a todos. La
  // diferencia son las horas de quien no entra (p. ej. ventas sin GitHub, con
  // sus reuniones del CRM): se le pasa a la tarjeta para que lo diga.
  const outsideExpected = useMemo(() => {
    if (!summary || !expected) return undefined;
    const out = summary.byUser.filter((u) => !expectedByUser.has(u.userId) && u.hours > 0);
    return {
      hours: Math.round(out.reduce((sum, u) => sum + u.hours, 0) * 100) / 100,
      names: out.map((u) => u.userName ?? u.userId),
    };
  }, [summary, expected, expectedByUser]);

  // #1344: el dinero de cada persona (solo dueños: `expected.cost` no viaja a nadie más).
  const costRows = expected?.cost?.byPerson ?? [];
  const costByUser = useMemo(() => new Map((expected?.cost?.byPerson ?? []).map((p) => [p.userId, p])), [expected]);
  // Tipo de cambio a MXN: el desglose da SIEMPRE el equivalente en pesos.
  const costFx = expected?.cost?.fx;
  const unpricedUsers = useMemo(() => new Set((expected?.cost?.unpricedMembers ?? []).map((p) => p.userId)), [expected]);

  // Pestañas del desglose, en su orden. La que no tiene datos no se ofrece; «Por
  // persona» siempre está (vacía dice «sin horas»). Si la elegida desaparece al
  // cambiar un filtro se cae a la primera disponible, y vuelve sola si reaparece.
  const hasRepoTab = summary?.repoBreakdown ? summary.repoBreakdown.byRepo.length > 0 : Boolean(summary?.byRepo.length);
  const breakdownTabs = BREAKDOWN_TABS.filter(
    (b) =>
      b.id === "person" ||
      (b.id === "squad" && Boolean(expected?.breakdown?.bySquad.length)) ||
      (b.id === "project" && Boolean(expected?.breakdown?.byProject.length)) ||
      (b.id === "kind" && Boolean(summary?.byKind?.length)) ||
      (b.id === "repo" && hasRepoTab),
  );
  const activeBreakdown: BreakdownTab = breakdownTabs.some((b) => b.id === breakdownTab) ? breakdownTab : breakdownTabs[0].id;

  const days = rangeDays(range.from, range.to);
  const card =`rounded-2xl border p-4 ${tc.cardBg} ${tc.border}`;
  const axisColor = isDarkMode ? "#9ca3af" : "#6b7280";

  return (
    <div className="w-full max-w-7xl overflow-y-auto p-4 sm:p-6 space-y-4">
      {/* Encabezado y filtros */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${tc.text}`}>{t("workHours.menu.dashboard")}</h1>
          <p className={`text-sm ${tc.textSecondary}`}>
            {data?.scope === "team" ? t("workHours.scopeTeam") : t("workHours.scopeMine")} · {t("workHours.rule")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(["today", "7d", "30d", "90d"] as const).map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`range-${p}`}
              onClick={() => {
                setPreset(p);
                setRange(presetRange(p));
              }}
              className={`rounded-lg px-3 py-2 text-xs font-medium ${
                preset === p ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary} border ${tc.border}`
              }`}
            >
              {t(`workHours.range.${p}`)}
            </button>
          ))}
          {(["qCurrent", "qPrevious"] as const).map((p) => {
            const period = payPeriodPreset(p);
            return (
              <button
                key={p}
                type="button"
                data-testid={`range-${p}`}
                title={t("workHours.range.payPeriodHint", { from: period.from, to: period.to, payDate: period.payDate })}
                onClick={() => {
                  setPreset(p);
                  setRange(presetRange(p));
                }}
                className={`rounded-lg px-3 py-2 text-xs font-medium ${
                  preset === p ? "bg-violet-600 text-white" : `${tc.buttonHover} ${tc.textSecondary} border ${tc.border}`
                }`}
              >
                {t(`workHours.range.${p}`)}
              </button>
            );
          })}
          <input
            type="date"
            aria-label={t("workHours.filters.from")}
            value={range.from}
            max={range.to}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, from: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          <input
            type="date"
            aria-label={t("workHours.filters.to")}
            value={range.to}
            min={range.from}
            max={today()}
            onChange={(e) => {
              setPreset("custom");
              setRange((r) => ({ ...r, to: e.target.value }));
            }}
            className={inputClass(isDarkMode)}
          />
          {me?.isAdmin && (
            <ClientScopeSelect
              value={clientId}
              onChange={(id) => {
                // El repo elegido puede no ser del cliente nuevo: se suelta.
                setRepo("");
                setClientId(id);
              }}
            />
          )}
          {me?.isAdmin && squadOptions && squadOptions.list.length > 0 && (
            <select
              aria-label={t("workHours.filters.squad")}
              data-testid="squad-scope-select"
              value={squadId}
              onChange={(e) => {
                // La persona elegida puede no ser del squad nuevo: se suelta.
                setUserId("");
                setSquadId(e.target.value);
              }}
              className={inputClass(isDarkMode)}
            >
              <option value="">{t("workHours.filters.allSquads")}</option>
              {squadOptions.list.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
              {/* «Sin squad» solo si hay alguien fuera de todo squad (o ya está elegido). */}
              {(squadOptions.hasNoSquad || squadId === "none") && <option value="none">{t("workHours.filters.noSquad")}</option>}
            </select>
          )}
          {me?.isAdmin && (
            <select aria-label={t("workHours.filters.user")} value={userId} onChange={(e) => setUserId(e.target.value)} className={inputClass(isDarkMode)}>
              <option value="">{t("workHours.filters.team")}</option>
              {(data?.users ?? []).map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.userName}
                </option>
              ))}
            </select>
          )}
          {/* El catálogo de repos que devuelve la API es el del RANGO, no el
              histórico: se dice en el grupo, y el repo ya elegido se conserva
              como opción aunque no tenga horas en el rango (si no, el filtro
              seguiría aplicado con el selector en blanco). */}
          <select
            aria-label={t("workHours.filters.repo")}
            title={t("workHours.filters.reposInRange")}
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            className={inputClass(isDarkMode)}
          >
            <option value="">{t("workHours.filters.allRepos")}</option>
            <optgroup label={t("workHours.filters.reposInRange")}>
              {(data?.repos ?? []).map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </optgroup>
            {repo && !(data?.repos ?? []).includes(repo) && (
              <option value={repo}>{t("workHours.filters.repoOutOfRange", { repo })}</option>
            )}
          </select>
        </div>
      </div>

      {/* Ámbar cuando solo se agotó el tiempo: el dato no llegó, no se sabe que
          falte. Y en los dos casos se puede recargar sin tocar los filtros. */}
      {errorKey && (
        <div
          data-testid="summary-error"
          className={`rounded-lg p-3 text-sm ${errorKey === "workHours.timeoutError" ? "bg-amber-500/10 text-amber-600" : "bg-red-500/10 text-red-500"}`}
        >
          <p>{t(errorKey)}</p>
          <button type="button" onClick={() => void load()} className="mt-2 rounded-md border border-current px-2 py-1 text-[11px] font-medium opacity-90">
            <i className="ri-refresh-line mr-1" />
            {t("workHours.retry")}
          </button>
        </div>
      )}
      {/* Lo que la API respondió distinto de lo que se pidió, dicho a tiempo. */}
      <SummaryNotices data={data} />
      {loading && !summary && <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.loading")}</p>}

      {summary && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            {[
              { label: t("workHours.kpi.hours"), value: `${summary.totalHours} h`, icon: "ri-time-line" },
              { label: t("workHours.kpi.avgPerDay"), value: `${Math.round((summary.totalHours / days) * 10) / 10} h`, icon: "ri-calendar-line" },
              { label: t("workHours.kpi.people"), value: String(summary.activeUsers), icon: "ri-team-line" },
              { label: t("workHours.kpi.commits"), value: String(summary.totalCommits), icon: "ri-git-commit-line" },
              {
                label: t("workHours.kpi.peak"),
                value: summary.peak ? `${weekdayLabel(summary.peak.weekday, locale)} ${hourLabel(summary.peak.hour)}` : "—",
                icon: "ri-fire-line",
              },
            ].map((k) => (
              <div key={k.label} className={card}>
                <p className={`flex items-center gap-1 text-xs ${tc.textSecondary}`}>
                  <i className={k.icon} /> {k.label}
                </p>
                <p className={`mt-1 text-2xl font-bold ${tc.text}`}>{k.value}</p>
                {/* #783: cuánto de las horas es minutos registrados y cuánto estimado.
                    #960: el estimado son bloques de tiempo real, no 1 h por hora con commit. */}
                {k.icon === "ri-time-line" && summary.recordedHours !== undefined && (
                  <p className={`mt-0.5 text-[11px] ${summary.estimatedHours ? "text-amber-500" : tc.textSecondary}`} data-testid="kpi-hours-split">
                    {t("workHours.kpi.split", { recorded: summary.recordedHours, estimated: summary.estimatedHours ?? 0 })}
                    {!!summary.estimatedHours && (
                      <i
                        className="ri-information-line ml-1 cursor-help align-middle"
                        title={t("workHours.estimateBlocks")}
                        aria-label={t("workHours.estimateBlocks")}
                        data-testid="kpi-estimate-explainer"
                      />
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* #969: esperado a hoy · real · diferencia (equipo o persona). */}
          {expected && <ExpectedKpis expected={expected} outside={outsideExpected} cardClass={card} textClass={tc.text} secondaryClass={tc.textSecondary} />}

          {/* Las mismas horas en dinero. Solo dueños: `cost` no viaja a nadie más. */}
          {expected?.cost && <ExpectedCostCard expected={expected} cardClass={card} textClass={tc.text} secondaryClass={tc.textSecondary} />}

          {/* Heatmap + detalle de la celda */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className={`${card} xl:col-span-2`}>
              <p className={`mb-1 text-sm font-semibold ${tc.text}`}>{t("workHours.heatmap.title")}</p>
              <p className={`mb-3 text-xs ${tc.textSecondary}`}>{t("workHours.heatmap.subtitle")}</p>
              <WorkHoursHeatmap grid={summary.heatmap} isDarkMode={isDarkMode} selected={cell} onSelect={openCell} />
            </div>
            <div className={card}>
              <p className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.detail.title")}</p>
              {!cell && <p className={`text-xs ${tc.textSecondary}`}>{t("workHours.detail.hint")}</p>}
              {cell && (
                <p className={`mb-2 text-xs ${tc.textSecondary}`}>
                  {weekdayLabel(cell.weekday, locale)} {hourLabel(cell.hour)} · {range.from} → {range.to}
                </p>
              )}
              {entriesLoading && (
                <p className={`text-xs ${tc.textSecondary}`} data-testid="hour-detail-loading">
                  {t("workHours.loading")}
                </p>
              )}

              {/* Error: se dice que falló y se ofrece reintentar. Nunca se pinta
                  el texto de «vacío» sobre un dato que no llegó. */}
              {entriesErrorKey && !entriesLoading && (
                <div
                  className={`rounded-lg p-3 text-xs ${
                    entriesErrorKey === "workHours.detail.timeout" ? "bg-amber-500/10 text-amber-600" : "bg-red-500/10 text-red-500"
                  }`}
                  data-testid="hour-detail-error"
                >
                  <p>{t(entriesErrorKey)}</p>
                  <button
                    type="button"
                    onClick={() => cell && void openCell(cell)}
                    className="mt-2 rounded-md border border-current px-2 py-1 text-[11px] font-medium opacity-90"
                  >
                    <i className="ri-refresh-line mr-1" />
                    {t("workHours.retry")}
                  </button>
                </div>
              )}

              {cell && entries && entries.length === 0 && !entriesLoading && !entriesErrorKey && (
                <p className={`text-xs ${tc.textSecondary}`}>{t("workHours.detail.empty")}</p>
              )}

              {/* Resumen de la hora: qué se hizo, no sólo cuántos commits hubo. */}
              {hasDetail && (
                <div className={`mb-3 rounded-xl border p-2 ${tc.border}`} data-testid="hour-detail-summary">
                  <p className={`text-xs font-semibold ${tc.text}`}>
                    {t("workHours.detail.summary", {
                      commits: digest.commits,
                      repos: digest.repos.length,
                      prs: digest.pullRequests.length,
                      people: digest.people.length,
                    })}
                  </p>
                  <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>
                    <span className="font-medium">{t("workHours.detail.reposLabel")}:</span>{" "}
                    {digest.repos.map((r, i) => {
                      const url = repoUrl(r);
                      return (
                        <span key={r}>
                          {i > 0 && ", "}
                          {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" title={t("workHours.repositories.openOnGithub", { repo: r })} className="text-violet-500 hover:underline">
                              {r}
                            </a>
                          ) : (
                            r
                          )}
                        </span>
                      );
                    })}
                  </p>
                  <p className={`mt-1 text-[11px] ${tc.textSecondary}`} data-testid="hour-detail-prs">
                    <span className="font-medium">{t("workHours.detail.prsLabel")}:</span>{" "}
                    {digest.pullRequests.length === 0
                      ? t("workHours.detail.noPrs")
                      : digest.pullRequests.map((p, i) => {
                          const url = pullRequestUrl(p);
                          const label = `${p.repo.split("/")[1] ?? p.repo}#${p.number}`;
                          return (
                            <span key={prKey(p)}>
                              {i > 0 && ", "}
                              {url ? (
                                <a href={url} target="_blank" rel="noopener noreferrer" title={p.title ?? prKey(p)} className="text-violet-500 hover:underline">
                                  {label}
                                </a>
                              ) : (
                                label
                              )}
                            </span>
                          );
                        })}
                  </p>
                  <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>
                    <span className="font-medium">{t("workHours.detail.peopleLabel")}:</span> {digest.people.join(", ")}
                  </p>
                </div>
              )}

              {/* Commits agrupados por repositorio y en lenguaje llano. */}
              <div className="max-h-[360px] space-y-3 overflow-y-auto pr-1" data-testid="hour-detail">
                {hasDetail &&
                  commitGroups.map((group) => {
                    const url = group.repo ? repoUrl(group.repo) : null;
                    return (
                      <div key={group.repo ?? "—"} className={`rounded-xl border p-2 ${tc.border}`} data-testid={`hour-detail-repo-${group.repo ?? "none"}`}>
                        <p className={`text-xs font-semibold ${tc.text}`}>
                          {group.repo === null ? (
                            t("workHours.detail.noRepo")
                          ) : url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" title={t("workHours.repositories.openOnGithub", { repo: group.repo })} className="text-violet-500 hover:underline">
                              {group.repo}
                            </a>
                          ) : (
                            group.repo
                          )}
                        </p>
                        <ul className="mt-1 space-y-2">
                          {group.commits.map((c) => {
                            const plain = plainCommitMessage(c.message);
                            return (
                              <li key={c.sha} className={`text-[11px] ${tc.textSecondary}`}>
                                {/* El mensaje manda; si no vino, se dice, no se inventa. */}
                                <p className={plain.text ? tc.text : "italic"}>
                                  {plain.text ?? t("workHours.detail.noMessage")}
                                </p>
                                <p className="mt-0.5 flex flex-wrap items-center gap-1 opacity-80">
                                  <a href={commitUrl(c) ?? undefined} target="_blank" rel="noopener noreferrer" className="font-mono text-violet-500 hover:underline">
                                    {c.sha.slice(0, 7)}
                                  </a>
                                  {plain.type && (
                                    <span className="rounded bg-gray-500/10 px-1 font-mono text-[10px] uppercase">
                                      {plain.scope ? `${plain.type}(${plain.scope})` : plain.type}
                                    </span>
                                  )}
                                  {c.person && <span>· {c.person}</span>}
                                  {c.authoredAt && <span>· {dateTimeLabel(c.authoredAt, locale)}</span>}
                                  {(c.pullRequests ?? []).map((p) => {
                                    const prUrl = pullRequestUrl(p);
                                    return (
                                      <span key={prKey(p)} className="rounded bg-violet-500/10 px-1 text-violet-500">
                                        {prUrl ? (
                                          <a href={prUrl} target="_blank" rel="noopener noreferrer" title={prKey(p)} className="hover:underline">
                                            #{p.number}
                                          </a>
                                        ) : (
                                          <>#{p.number}</>
                                        )}
                                      </span>
                                    );
                                  })}
                                </p>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Asignación a proyectos (#573) */}
          {allocation ? (
            <AllocationCard data={allocation} isDarkMode={isDarkMode} cardClass={card} textClass={tc.text} secondaryClass={tc.textSecondary} />
          ) : allocationErrorKey ? (
            <div className="rounded-lg bg-red-500/10 p-3 text-xs text-red-500" data-testid="allocation-error">
              <p>{t(allocationErrorKey)}</p>
              <button type="button" onClick={() => setAllocationReload((n) => n + 1)} className="mt-2 rounded-md border border-red-500/40 px-2 py-1 text-[11px] font-medium">
                <i className="ri-refresh-line mr-1" />
                {t("workHours.retry")}
              </button>
            </div>
          ) : (
            me?.isAdmin && !userId && <p className={`text-xs ${tc.textSecondary}`}>{t("workHours.allocation.pickPerson")}</p>
          )}

          {/* Serie de horas */}
          <div className={card}>
            <div className="mb-3 flex items-center justify-between">
              <p className={`text-sm font-semibold ${tc.text}`}>{t("workHours.series.title")}</p>
              <div className="flex gap-1">
                {(["day", "week", "month"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSeries(s)}
                    className={`rounded-md px-2 py-1 text-xs ${series === s ? "bg-violet-600 text-white" : `${tc.textSecondary} ${tc.buttonHover}`}`}
                  >
                    {t(`workHours.series.${s}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#374151" : "#e5e7eb"} vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: axisColor, fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: axisColor, fontSize: 11 }} />
                  <Tooltip formatter={(v) => [`${v} h`, t("workHours.kpi.hours")]} />
                  <Bar dataKey="hours" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="space-y-4">
            {/* #1344: los renglones de cada desglose se abren con las líneas que los
                suman (`detail`); mientras cargan, no se despliegan. */}
            {detailFailed && (
              <p className="text-xs text-amber-600" data-testid="detail-error">
                {t("workHours.ledger.loadError")}{" "}
                <button type="button" className="underline" onClick={() => void load()} data-testid="detail-retry">
                  {t("workHours.reload")}
                </button>
              </p>
            )}
            {detailTooLarge && (
              <p className={`text-xs ${tc.textSecondary}`} data-testid="detail-too-large">
                {t("workHours.ledger.tooLarge")}
              </p>
            )}
            {/* Desglose de horas: una sola tarjeta con una pestaña por corte. Solo
                se monta el panel elegido, y la pestaña sin datos no se ofrece. */}
            <div className={card} data-testid="breakdown-card">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className={`text-sm font-semibold ${tc.text}`}>{t("workHours.breakdown.title")}</p>
                <div className="flex flex-wrap gap-1" role="tablist" aria-label={t("workHours.breakdown.title")} data-testid="breakdown-tabs">
                  {breakdownTabs.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      role="tab"
                      id={`breakdown-tab-${b.id}`}
                      aria-selected={activeBreakdown === b.id}
                      aria-controls="breakdown-panel"
                      data-testid={`breakdown-tab-${b.id}`}
                      onClick={() => setBreakdownTab(b.id)}
                      className={`rounded-md px-2 py-1 text-xs ${activeBreakdown === b.id ? "bg-violet-600 text-white" : `${tc.textSecondary} ${tc.buttonHover}`}`}
                    >
                      {t(b.label)}
                    </button>
                  ))}
                </div>
              </div>
              <div role="tabpanel" id="breakdown-panel" aria-labelledby={`breakdown-tab-${activeBreakdown}`} data-testid="breakdown-panel">
            {/* Esperado · real · diferencia por squad (con «Sin squad») y por proyecto. */}
            {expected && (activeBreakdown === "squad" || activeBreakdown === "project") && (
              <ExpectedBreakdownTable expected={expected} by={activeBreakdown} textClass={tc.text} secondaryClass={tc.textSecondary} />
            )}
            {/* Personas. Para los dueños, además, el dinero de cada persona (#1344):
                lo que valen sus horas, lo esperado a pagar a hoy y la pérdida por
                las horas que faltan. `cost` solo viaja a los dueños. */}
            {activeBreakdown === "person" && (
            <div data-testid="people-card">
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`text-left text-xs ${tc.textSecondary}`}>
                    <th className="py-1">{t("workHours.people.person")}</th>
                    <th className="py-1 text-right">{t("workHours.kpi.hours")}</th>
                    <th className="py-1 text-right">{t("workHours.people.days")}</th>
                    <th className="py-1 text-right">{t("workHours.kpi.commits")}</th>
                    <th className="py-1 text-right">{t("workHours.people.window")}</th>
                    {expected && (
                      <>
                        <th className="py-1 text-right" title={expectedRuleText}>
                          {t("workHours.expected.expected")}
                        </th>
                        <th className="py-1 text-right">{t("workHours.expected.actual")}</th>
                        <th className="py-1 text-right">{t("workHours.expected.gap")}</th>
                      </>
                    )}
                    {costRows.length > 0 && (
                      <>
                        <th className="py-1 pl-2 text-right">{t("workHours.people.workedCost")}</th>
                        <th className="py-1 pl-2 text-right">{t("workHours.people.expectedCost")}</th>
                        <th className="py-1 pl-2 text-right">{t("workHours.people.lossCost")}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {peopleRows.map((u) => (
                    <tr key={u.userId} className={`border-t ${tc.border} ${tc.text}`} data-testid={`people-row-${u.userId}`}>
                      <td className="py-1.5">
                        {me?.isAdmin ? (
                          <button type="button" className="text-violet-500 hover:underline" onClick={() => setUserId(u.userId)}>
                            {u.userName ?? u.userId}
                          </button>
                        ) : (
                          u.userName ?? u.userId
                        )}
                      </td>
                      <td className="py-1.5 text-right font-semibold">{u.hours} h</td>
                      <td className="py-1.5 text-right">{u.days}</td>
                      <td className="py-1.5 text-right">{u.commits}</td>
                      <td className="py-1.5 text-right">
                        {u.firstHour === null ? "—" : `${hourLabel(u.firstHour)}–${hourLabel((u.lastHour ?? 0) + 1)}`}
                      </td>
                      {expected &&
                        (() => {
                          const e = expectedByUser.get(u.userId);
                          // Sin línea: no entra al esperado (contratista, sin asignaciones…).
                          if (!e) {
                            return (
                              <td colSpan={3} className={`py-1.5 text-right text-xs ${tc.textSecondary}`}>
                                {t("workHours.expected.notCounted")}
                              </td>
                            );
                          }
                          return (
                            <>
                              <td className="py-1.5 text-right" data-testid={`people-expected-${u.userId}`}>
                                {e.expectedHours} h
                              </td>
                              <td className="py-1.5 text-right">
                                {e.actualHours} h
                                {/* #1281: horas por aprobar de la persona, fuera del real. */}
                                {typeof e.pendingHours === "number" && e.pendingHours > 0 && (
                                  <span className="block whitespace-nowrap text-[11px] text-amber-600" title={t("workHours.expected.pendingHint")} data-testid={`people-pending-${u.userId}`}>
                                    {t("workHours.expected.pendingHours", { hours: e.pendingHours })}
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 text-right">
                                <GapBadge gapHours={e.gapHours} testId={`people-gap-${u.userId}`} />
                                {typeof e.pendingHours === "number" && e.pendingHours > 0 && (
                                  <GapWithPending gapWithPendingHours={e.gapWithPendingHours} pendingHours={e.pendingHours} testId={`people-gap-with-pending-${u.userId}`} />
                                )}
                              </td>
                            </>
                          );
                        })()}
                      {costRows.length > 0 &&
                        (() => {
                          const c = costByUser.get(u.userId);
                          // Sin importe: o no tiene sueldo registrado (se dice), o no entra
                          // al esperado (contratista, sin asignaciones): ahí solo «—», porque
                          // decir «sin sueldo» de quien sí lo tiene sería falso.
                          if (!c) {
                            return (
                              <td colSpan={3} className={`py-1.5 pl-2 text-right text-xs ${tc.textSecondary}`} data-testid={`people-cost-none-${u.userId}`}>
                                {unpricedUsers.has(u.userId) ? t("workHours.people.noCost") : "—"}
                              </td>
                            );
                          }
                          return (
                            <>
                              <td className={`whitespace-nowrap py-1.5 pl-2 text-right ${MONEY_PRIVACY_CLASS}`} data-testid={`people-cost-worked-${u.userId}`}>
                                {money(c.actualCost, c.currency, i18n?.language)}
                                <MxnEquivalent amount={c.actualCost} currency={c.currency} fx={costFx} className={tc.textSecondary} testId={`people-cost-worked-mxn-${u.userId}`} />
                              </td>
                              <td className={`whitespace-nowrap py-1.5 pl-2 text-right ${MONEY_PRIVACY_CLASS}`} data-testid={`people-cost-expected-${u.userId}`}>
                                {money(c.expectedCost, c.currency, i18n?.language)}
                                <MxnEquivalent amount={c.expectedCost} currency={c.currency} fx={costFx} className={tc.textSecondary} testId={`people-cost-expected-mxn-${u.userId}`} />
                              </td>
                              <td className={`whitespace-nowrap py-1.5 pl-2 text-right ${MONEY_PRIVACY_CLASS} ${c.lossCost > 0 ? "font-semibold text-red-500" : ""}`} data-testid={`people-cost-loss-${u.userId}`}>
                                {c.lossCost > 0 ? money(c.lossCost, c.currency, i18n?.language) : "—"}
                                {c.lossCost > 0 && <MxnEquivalent amount={c.lossCost} currency={c.currency} fx={costFx} testId={`people-cost-loss-mxn-${u.userId}`} />}
                                {c.lossCost > 0 && <span className={`block text-[11px] font-normal ${tc.textSecondary}`}>{t("workHours.people.shortfall", { hours: c.shortfallHours })}</span>}
                              </td>
                            </>
                          );
                        })()}
                    </tr>
                  ))}
                  {peopleRows.length === 0 && (
                    <tr>
                      <td colSpan={(expected ? 8 : 5) + (costRows.length > 0 ? 3 : 0)} className={`py-3 text-center text-xs ${tc.textSecondary}`}>
                        {t("workHours.empty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
              {costRows.length > 0 && <p className={`mt-2 text-[11px] ${tc.textSecondary}`}>{t("workHours.people.costHint")}</p>}
            </div>
            )}
              {/* Reuniones del CRM, ventas, marketing y desarrollo, cada tipo aparte. */}
              {activeBreakdown === "kind" && summary.byKind && (
                <KindHoursTable byKind={summary.byKind} catalog={kindCatalog} ledger={detail} tc={tc} showPeople={summary.byUser.length > 1} hideTitle />
              )}
              {/* Con el desglose del servidor, cada repo trae lo que cuenta y lo que
                  demuestran sus commits. Un backend anterior no lo manda: se pinta
                  la lista de siempre. */}
              {activeBreakdown === "repo" && summary.repoBreakdown && (
                <RepoBreakdown breakdown={summary.repoBreakdown} ledger={detail} catalog={kindCatalog} tc={tc} isDarkMode={isDarkMode} hideTitle />
              )}
              {activeBreakdown === "repo" && !summary.repoBreakdown && (
                <div data-testid="repo-list">
                  {summary.byRepo.map((r) => {
                    const url = repoUrl(r.repo);
                    return (
                      <div key={r.repo} className="mb-1.5">
                        <div className={`flex justify-between text-xs ${tc.textSecondary}`}>
                          {url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" title={t("workHours.repositories.openOnGithub", { repo: r.repo })} className="text-violet-500 hover:underline">
                              {r.repo}
                            </a>
                          ) : (
                            <span>{r.repo}</span>
                          )}
                          <span>{r.hours} h</span>
                        </div>
                        <div className={`h-1.5 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                          <div className="h-1.5 rounded-full bg-violet-500" style={{ width: `${Math.min(100, (r.hours / Math.max(1, summary.totalHours)) * 100)}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
              {/* Con qué tipo de cambio se pasó a MXN lo que se paga en otra moneda:
                  vale para todas las pestañas. Solo dueños (`cost` no viaja a nadie más). */}
              {expected?.cost && <FxNote fx={costFx} fxMissing={expected.cost.fxMissing} className={tc.textSecondary} />}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* PRs: por repositorio, y al abrirlo, los PRs que se cuentan. */}
              <div className={card}>
                <PullRequestsCard
                  pullRequests={summary.pullRequests}
                  hoursWithoutPullRequest={summary.hoursWithoutPullRequest}
                  activityNote={
                    summary.repoBreakdown && summary.repoBreakdown.countedMinutes !== summary.repoBreakdown.activityMinutes
                      ? t("workHours.prs.activityNote", { activity: hoursOf(summary.repoBreakdown.activityMinutes), counted: hoursOf(summary.repoBreakdown.countedMinutes) })
                      : null
                  }
                  ledger={detail}
                  showPeople={Boolean(me?.isAdmin)}
                  tc={tc}
                />
              </div>
              {/* Por qué el total no coincide con la suma por repositorio y por PR. */}
              {summary.repoBreakdown && summary.repoBreakdown.countedMinutes !== summary.repoBreakdown.activityMinutes && (
                <div className={card}>
                  <BridgeCard breakdown={summary.repoBreakdown} ledger={detail} catalog={kindCatalog} tc={tc} />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
